import { createSlice, PayloadAction } from '@reduxjs/toolkit';
import { asyncActions } from '@/Pages/Products/services/transfer/asyncActions';
import { ProductErrorModel, ProductsTransferPointsModel, ProductTransferModel } from '@/Models';
import { TransferModalType } from '@/Pages/Products/types/TransferModalType';
import { DataTableSource } from '@/Components/DataTableV2/services/types';
import { ProductTransferTableRow } from '@/Types';
import { productReportMapping } from '@/Mapping/ProductReportMapping';
import { TransferReportTableRow } from '@/Types/TransferReportTableRow';
import { productMapping } from '@/Mapping/ProductMapping';

export type TransferState = {
  transferPoints: ProductsTransferPointsModel;
  transferProducts: TransferModalType;
  productReports: {
    reports: TransferReportTableRow[];
    selectedReportIds: number[];
    isDeleteModalOpen: boolean;
  };
  transferErrors: ProductErrorModel[];
};

const initialState: TransferState = {
  transferPoints: { receipentWarehouse: '', senderWarehouse: '' },
  transferProducts: {
    products: [],
    productsDataSource: null,
    isTransferModalOpen: false,
    countOfPendingRequests: 0,
    needDeleteProductSku: null,
    nextReportNumber: null,
  },
  productReports: {
    reports: [],
    selectedReportIds: [],
    isDeleteModalOpen: false,
  },
  transferErrors: [],
};

const transferSlice = createSlice({
  name: 'transfer',
  initialState,
  reducers: {
    setTransferPoints(state, action: PayloadAction<ProductsTransferPointsModel>) {
      state.transferPoints = action.payload;
    },
    setVisibleTransferModal(state, action: PayloadAction<boolean>) {
      state.transferProducts.isTransferModalOpen = action.payload;
    },
    initTransferProducts(state) {
      state.transferProducts.productsDataSource = new DataTableSource<ProductTransferTableRow>('sku', []);
    },
    clearTransferProducts(state) {
      state.transferProducts = {
        ...state.transferProducts,
        products: [],
        countOfPendingRequests: 0,
      };

      state.transferProducts.productsDataSource?.clear();
    },
    addTransferProducts(state, action: PayloadAction<ProductTransferModel>) {
      const { product, quantity } = action.payload;
      const productIndex = state.transferProducts.products.findIndex((item) => item.sku == product.sku);

      if (state.transferProducts.countOfPendingRequests != 0) {
        state.transferProducts.countOfPendingRequests = state.transferProducts.countOfPendingRequests - 1;
      }

      if (productIndex != -1) {
        const product = state.transferProducts.products[productIndex];
        const newQuantity = Number(product.quantity) + Number(quantity);

        state.transferProducts.products[productIndex].quantity = newQuantity;

        state.transferProducts.productsDataSource?.update([
          {
            key: product.sku,
            data: { ...product, quantity: newQuantity },
          },
        ]);
      } else {
        const newProduct = productMapping.convertProductToProductTransferTableRow(product, quantity, true);
        const updatedProducts = state.transferProducts.products.map((item) => ({
          ...item,
          isLastModified: false,
        }));

        state.transferProducts.products = [...updatedProducts, newProduct];

        state.transferProducts.productsDataSource?.update(
          updatedProducts.map((product) => ({
            key: product.sku,
            data: { ...product },
          })),
        );

        state.transferProducts.productsDataSource?.insert([newProduct]);
      }
    },
    changeTransferProduct(state, action: PayloadAction<{ sku: string; data: ProductTransferTableRow }>) {
      const { sku, data } = action.payload;

      state.transferProducts.products = state.transferProducts.products.map((item) => {
        if (item.sku == sku) return { ...item, ...data, isLastModified: true };
        return { ...item, isLastModified: false };
      });

      state.transferProducts.productsDataSource?.update(
        state.transferProducts.products.map((product) => ({
          key: product.sku,
          data: { ...product },
        })),
      );
    },
    setNeedDeleteProductSku(state, action: PayloadAction<string>) {
      state.transferProducts.needDeleteProductSku = action.payload;
    },
    deleteTransferProduct(state, action: PayloadAction<{ sku: string }>) {
      const { sku } = action.payload;

      state.transferProducts.products = state.transferProducts.products.filter((item) => {
        return item.sku != sku;
      });
      state.transferProducts.productsDataSource?.remove([sku]);
    },
    changeCountOfPendingRequests(state, action: PayloadAction<number>) {
      state.transferProducts.countOfPendingRequests = action.payload;
    },

    // Reports
    setVisibleDeleteReportModal(state, action: PayloadAction<boolean>) {
      state.productReports.isDeleteModalOpen = action.payload;
    },
    setSelectedReports: (state, action: PayloadAction<TransferReportTableRow[]>) => {
      state.productReports.selectedReportIds = action.payload.map((item) => item.id);
    },
  },
  extraReducers: (builder) => {
    builder
      // Getting reports
      .addCase(asyncActions.getProductReports.fulfilled, (state, action) => {
        state.productReports.reports = action.payload.productReports.map(productReportMapping.modelToTableRow);
      })
      .addCase(asyncActions.deleteProductReports.fulfilled, (state, action) => {
        state.productReports.reports = state.productReports.reports.filter((x) => !action.payload.includes(x.id));
      })
      // Getting transfer errors
      .addCase(asyncActions.getProductTransferErrors.fulfilled, (state, action) => {
        state.transferErrors = action.payload.errorModels;
      })
      .addCase(asyncActions.clearProductTransferErrors.fulfilled, (state) => {
        state.transferErrors = [];
      })
      .addCase(asyncActions.getNextReportNumber.fulfilled, (state, action) => {
        state.transferProducts.nextReportNumber = action.payload.nextReportNumber;
      });
  },
});

const { actions, reducer } = transferSlice;

export const transferPageActions = actions;
export const transferPageReducer = reducer;
export const transferAsyncActions = asyncActions;
